import type { MakerAgent } from '@ralph/contracts'
import { Link } from 'react-router-dom'
import { AgentAvatar } from '@/components/marketplace/consumer/AgentAvatar.tsx'
import { Button } from '@/components/ui/primitives.tsx'
import { useRespondToInvite } from '@/hooks/use-maker.ts'
import { useT } from '@/i18n/index.ts'

/**
 * Invitations waiting on the current user.
 *
 * Derived from the maker list rather than fetched separately: an invitation IS a co-editor
 * grant in the `invited` state on an agent the caller can already see, so a dedicated
 * endpoint would be a second source of truth for one boolean. Accepting is the invitee's
 * own act — nothing about the agent changes until they take it (AGM-026), and they can look
 * at it first: they talk to it and change it only once they accept (PRD-26 D-53).
 */
export const pendingInvitesFor = (agents: MakerAgent[], userId: string | undefined): MakerAgent[] =>
  userId === undefined
    ? []
    : agents.filter((agent) => agent.editors.some((editor) => editor.userId === userId && editor.status === 'invited'))

export const InvitesCard = ({ invites }: { invites: MakerAgent[] }) => {
  // Before the early return, because it is a hook; in the body, because a module-scope capture
  // renders the previous language with nothing failing (PRD-27 F-3).
  const strings = useT().marketplace.maker.invites
  const respond = useRespondToInvite()

  if (invites.length === 0) return null

  return (
    <div className="card rounded-panel px-4 py-3.5">
      <h2 className="font-sans text-[14px] font-semibold tracking-[-0.018em]">{strings.title}</h2>
      <p className="mt-1.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{strings.lead}</p>

      <ul className="mt-3 space-y-2">
        {invites.map((agent) => (
          <li
            key={agent.agentId}
            className="flex flex-wrap items-center gap-2.5 rounded-tile border border-rule bg-card-solid px-3 py-2.5"
          >
            <AgentAvatar icon={agent.icon} displayName={agent.displayName} accent={agent.accent} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13.5px] font-semibold tracking-[-0.014em]">{agent.displayName}</p>
              <p className="readout truncate text-fg-subtle">
                {agent.owner.kind === 'group'
                  ? strings.fromGroup(agent.owner.group.name)
                  : strings.fromPerson(agent.owner.name)}
              </p>
            </div>
            <Link
              to={`/agents/${encodeURIComponent(agent.agentId)}/edit`}
              className="text-[12.5px] text-fg-muted underline-offset-2 hover:text-fg hover:underline"
            >
              {strings.look}
            </Link>
            <Button
              size="sm"
              variant="primary"
              disabled={respond.isPending}
              onClick={() => respond.mutate({ agentId: agent.agentId, decision: 'accept' })}
            >
              {strings.accept}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={respond.isPending}
              onClick={() => respond.mutate({ agentId: agent.agentId, decision: 'decline' })}
            >
              {strings.decline}
            </Button>
          </li>
        ))}
      </ul>

      {respond.isError && (
        <p role="alert" className="mt-2 text-[12.5px] text-alert">
          {strings.failed}
        </p>
      )}
    </div>
  )
}
